import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import ProgressBar from '../../components/charts/progressBar';
import CommonCard from '../../components/card/CommonCard';
import { Next, Prev, UserEmblem } from '../../assets/images';
import { useDispatch } from 'react-redux';
import { logAPI } from '../../actions/imsServer';
import { formatDateTimeDisplay } from '../../utils/base';
import { API_PAGE_SIZE } from '../../constants';
import { useLevels } from '../../context/levels';
import StatisticsChart from '../../components/charts/barChart/StatisticsChart';
import RewardHistoryItem from '../../components/rewardHistoryItem';

// 공통 섹션 컨테이너 스타일
const SectionContainerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%'
};

export const UserExp = props => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const dispatch = useDispatch();

    const [pointLogData, setPointLogData] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { userLevels } = useLevels();

    // 이달의 차트 데이터 생성
    const monthChartData = React.useMemo(() => {
        // 선택된 달의 모든 날짜 생성
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const chartDataMap = new Map();

        // 모든 날짜를 0으로 초기화
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            chartDataMap.set(dateStr, { day: dateStr, value: 0 });
        }

        // pointLogData를 날짜별로 합산
        pointLogData.forEach(log => {
            if (log.time) {
                const dateStr = log.time.substring(0, 10); // YYYY-MM-DD 형식
                if (chartDataMap.has(dateStr)) {
                    chartDataMap.get(dateStr).value += log.point || 0;
                }
            }
        });
        return Array.from(chartDataMap.values());
    }, [pointLogData, currentDate]);

    // 최대값, 최소값, 평균값 계산
    const yAxisData = React.useMemo(() => {
        if (!monthChartData || monthChartData.length === 0) {
            return { maxValue: 0, minValue: 0, averageValue: 0 };
        }

        const values = monthChartData.map(item => item.value);
        const maxValue = Math.max(...values, 0);
        const minValue = 0;
        const averageValue = values.reduce((sum, val) => sum + val, 0) / values.length;

        return {
            maxValue,
            minValue,
            averageValue
        };
    }, [monthChartData]);

    const [meInfo, setMeInfo] = useState(JSON.parse(localStorage.getItem('meInfo')));

    useEffect(() => {
        // Load initial user data from localStorage
        const loadUserDataFromStorage = () => {
            try {
                const meInfo = localStorage.getItem('meInfo');
                setMeInfo(JSON.parse(meInfo));
            } catch (error) {
                console.error('Failed to load user data from localStorage:', error);
            }
        };

        loadUserDataFromStorage();
    }, []);

    // currentDate 변경 시 pointLogData 다시 로드
    useEffect(() => {
        const loadPointLogData = async () => {
            // 선택된 달 시작일과 종료일 계산
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const startDate = new Date(year, month, 1);
            const endDate = new Date(year, month + 1, 0, 23, 59, 59);

            const startTime = startDate.toISOString();
            const endTime = endDate.toISOString();
            let allLogs = [];
            let offset = 0;
            let page;
            do {
                const res = await logAPI.getPointLogs({
                    userId: meInfo.id,
                    type: 0,
                    startTime,
                    endTime,
                    offset
                });
                page = res?.data?.logs || [];
                allLogs = allLogs.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);
            setPointLogData(allLogs);
        }

        if (meInfo?.id) {
            loadPointLogData();
        }
    }, [currentDate, meInfo?.id]);

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        const now = new Date();
        const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);

        // 현재 월을 넘어가지 않도록 제한
        if (nextMonth.getFullYear() < now.getFullYear() ||
            (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() <= now.getMonth())) {
            setCurrentDate(nextMonth);
        }
    };

    const currentDateString = formatDateTimeDisplay(currentDate, 'yearMonth');

    // 다음 버튼 비활성화 여부 확인
    const now = new Date();
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const isNextDisabled = nextMonth.getFullYear() > now.getFullYear() ||
        (nextMonth.getFullYear() === now.getFullYear() && nextMonth.getMonth() > now.getMonth());

    // 현재 선택된 달의 포인트 로그 필터링
    const filteredPointLogs = React.useMemo(() => {
        if (!pointLogData || pointLogData.length === 0) return [];

        const selectedYear = currentDate.getFullYear();
        const selectedMonth = currentDate.getMonth();

        return pointLogData.filter(log => {
            if (!log.time) return false;
            const logDate = new Date(log.time);
            return logDate.getFullYear() === selectedYear && logDate.getMonth() === selectedMonth;
        });
    }, [pointLogData, currentDate]);

    return (
        <ContentArea sx={{ padding: '0 10px 20px 10px' }}>

            {/* Main Content */}
            <Box sx={{
                flex: 1,
                ...SectionContainerStyle,
                padding: '0 0px 30px 0px'
            }}>
                {/* Level Progress Section */}
                <CommonCard >
                    <Typography sx={{
                        fontSize: '25px',
                        fontWeight: 700,
                        letterSpacing: '-0.75px'
                    }}>
                        LV.{meInfo?.level || 0}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        alignSelf: 'stretch',
                    }}>
                        {/* Current Level Icon */}
                        <Box sx={{
                            display: 'flex',
                            width: '60px',
                            height: '60px',
                            padding: '8px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            aspectRatio: '1/1',
                        }}>
                            <UserEmblem
                                level={meInfo?.level || 1}
                                size={44}
                            />
                        </Box>

                        {/* Progress Bar */}
                        <ProgressBar
                            rangeStart={0}
                            rangeEnd={meInfo?.level > 99 ? 50000 : userLevels?.find(level => level.level === meInfo?.level)?.requiredExp || 0}
                            start={0}
                            end={meInfo?.level > 99 ? 50000 : (meInfo?.exp - (userLevels?.find(level => level.level === meInfo?.level - 1)?.totalExp)) || 0}
                            height="20px"
                            showCurrentIndicator={false}
                            showProgress={true}
                            showValueLabel={true}
                            progressColor={'#18CB00'}
                            sx={{ alignSelf: 'center' }}
                        />

                        {/* Next Level Icon */}
                        <Box sx={{
                            display: 'flex',
                            width: '60px',
                            height: '60px',
                            padding: '0 5px',
                            justifyContent: 'center',
                            alignItems: 'center',
                            aspectRatio: '1/1',
                        }}>
                            <UserEmblem
                                level={Math.min((meInfo?.level || 1) + 1, 100)}
                                size={50}
                            />
                        </Box>
                    </Box>
                </CommonCard>

                <CommonCard>
                    <StatisticsChart
                        activeView="month"
                        chartData={monthChartData}
                        yAxisData={yAxisData}
                        barColorType="average"
                    />
                </CommonCard>

                {/* Date Indicator */}
                <Box sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 0'
                }}>
                    <Box
                        onClick={handlePrevMonth}
                        sx={{
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Prev color={theme.palette.figma.white[80]} size={20} />
                    </Box>
                    <Typography sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        color: theme.palette.figma.white[80],
                        letterSpacing: '-0.48px',
                        whiteSpace: 'nowrap'
                    }}>
                        {currentDateString}
                    </Typography>
                    <Box
                        onClick={() => !isNextDisabled && handleNextMonth()}
                        sx={{
                            width: '20px',
                            height: '20px',
                            cursor: isNextDisabled ? 'default' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: isNextDisabled ? 0.3 : 1
                        }}
                    >
                        <Next color={theme.palette.figma.white[80]} size={20} />
                    </Box>
                </Box>

                {/* Reward History List */}
                {filteredPointLogs.length > 0 && (
                    <Box sx={{ width: '100%' }}>
                        {filteredPointLogs.map((log, index) => (
                            <RewardHistoryItem key={log.id || index} item={log} showSign={false} />
                        ))}
                    </Box>
                )}
            </Box >
        </ContentArea >
    );
};
